<template>
  <transition name="fade">
    <div v-if="productData" class="root" :class="{ 'root--mobile': isMobile, 'root--desktop': !isMobile }"
      @click.self="close">
      <!-- Desktop toast -->
      <div v-if="!isMobile" class="toast" role="status" aria-live="polite">
        <div class="toast-thumb">
          <lazy-image :item="image" class="toast-image"/>
        </div>

        <div class="toast-inner">
          <span class="toast-status font-smaller">
            {{ $t('Successfully added to cart') }}
          </span>
          <localized-url class="toast-link link-light" url="/cart" @click.native="popupGTM('view the cart')">
            {{ $t('View cart') }}
          </localized-url>
          <button type="button" class="close" aria-label="Close add to cart notification" @click="close">
            <i class="icon-close"></i>
          </button>
        </div>

        <div class="toast-timer"></div>
      </div>

      <!-- Mobile modal -->
      <div v-else class="overlay" role="dialog" aria-modal="true" aria-label="Added to cart" v-bsl="true">
        <div class="modal">
          <button type="button" class="close close--modal" aria-label="Close add to cart modal" @click="close" ref="closeButton">
            <i class="icon-close"></i>
          </button>

          <div class="modal-content">
            <h3 class="modal-title">{{ $t('Added to the cart') }}</h3>

            <div class="modal-image-wrapper">
              <lazy-image :item="image" class="modal-image"/>
            </div>

            <div class="modal-text">
              <h6 class="modal-title">{{ productData.name }}</h6>

              <p class="modal-product-info">
                <span v-if="productData.ref"> {{ $t('Ref') }} {{ productData.ref }} </span>
                <span v-if="productData.wrist_size || productData.diameter" class="modal-info-row">
                  <span v-if="productData.wrist_size"> {{ $t('Wrist size') }} {{ productData.wrist_size }}</span>
                  <span v-if="productData.wrist_size && productData.diameter" class="modal-divider" aria-hidden="true"></span>
                  <span v-if="productData.diameter"> Ø {{ productData.diameter }} mm </span>
                </span>
              </p>

              <div class="font-btn">
                {{ productData.price | currency}}
              </div>
            </div>

            <div class="button-wrapper">
              <localized-url class="button btn" url="/checkout" @click.native="popupGTM('proceed to checkout'), close()">
                {{ $t('Proceed to checkout') }}
              </localized-url>
              <localized-url class="button btn -stroke" url="/cart" @click.native="popupGTM('view the cart'), close()">
                {{ $t('View cart') }}
              </localized-url>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import LocalizedUrl from './utils/LocalizedUrl.vue';
import LazyImage from './utils/LazyImage'
import ImageManager from 'src/modules/prismic/utilities/ImageManager'

export default {
  name: 'AddToCartNotification',

  components: {
    LocalizedUrl,
    LazyImage
  },

  data () {
    return {
      productData: null,
      isMobile: false,
      hideTimer: null,
      barKey: 0,
      mobileBreakpoint: 767
    };
  },

  mounted () {
    this.setViewportMode();
    window.addEventListener('resize', this.setViewportMode, { passive: true });
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.setViewportMode);
    this.clearHideTimer();
    this.$bus.$off('add-to-cart-notification', this.show);
  },

  created () {
    this.$bus.$on('add-to-cart-notification', this.show);
  },

  methods: {
    setViewportMode () {
      this.isMobile = window.innerWidth <= this.mobileBreakpoint;
    },

    show (productData) {
      setTimeout(() => {
        this.$refs.closeButton?.focus();
      }, 100)

      this.clearHideTimer();

      this.productData = productData;

      if (!this.isMobile) {
        this.hideTimer = setTimeout(() => {
          this.close();
        }, 4000);
      }
    },

    close () {
      this.clearHideTimer();
      this.productData = null;
    },

    clearHideTimer () {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    },

    popupGTM(ctaName) {
      this.$gtm.trackEvent({
        event: 'add_to_cart_popup',
        product_name: this.productData.name,
        cta_name: ctaName
      })
    }
  },

  computed: {
    image() {
      return {
        url: this.productData.image,
        lowDefinitionUrl: ImageManager.getLowDefinition(this.productData.image, 150, 1.25, true, 4),
        srcset: ImageManager.getSrcSet(this.productData.image, 1200, 200, 100, 1.25, true),
        alt: this.productData.name || '',
      }
    }
  }
};
</script>

<style lang="scss" scoped>

.root {
  --level: 2001;
}

/* Fade */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.22s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.close {
  position: relative;
  padding: 0;
  border: 0;
  background: transparent;
  width: 2.2rem;
  height: 2.2rem;
}

.toast-timer {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 3px;
  background: var(--zenith-theme-primary);
  transform-origin: right center;
  animation: countdown 4s linear forwards;
}

@keyframes countdown {
  from {
    transform: scaleX(1);
  }

  to {
    transform: scaleX(0);
  }
}

/* Desktop toast */

.root--desktop {
  position: fixed;
  top: 11rem;
  right: 6.1rem;
  z-index: var(--level);

  .toast {
    position: relative;
    width: fit-content;
    background: var(--zenith-white);
    border: 1px solid var(--zenith-grey-medium);
    border-radius: 0.5rem;
    overflow: hidden;
    display: flex;

    .toast-thumb {
      height: 4.8rem;
      aspect-ratio: 1 / 1;
      display: flex;
      justify-content: center;
      overflow: hidden;
      background: var(--zenith-grey-light);

      .toast-image {
        /deep/ img {
          height: 100%;
        }
      }
    }

    .toast-inner {
      display: flex;
      align-items: center;
      padding-inline: 2rem;
      gap: 1.5rem;

      .toast-status {
        text-transform: uppercase;
        max-width: 11.2rem;
      }

      .toast-link {
        margin: 0;
        padding: 0;
        font-size: 1.2rem;
        text-transform: uppercase;
      }
    }
  }
}

/* Mobile modal */

.root--mobile {
  position: fixed;
  width: 100vw;
  height: 100vh;
  bottom: 0;
  z-index: var(--level);

  .overlay {
    width: 100%;
    height: 100%;
    background-color: color-mix(in srgb, var(--zenith-theme-primary) 25%, transparent);
    display: flex;
    flex-direction: column;
    justify-content: end;
    
    .modal {
      position: relative;
      width: 100%;
      background: var(--zenith-white);
      padding: 3.875rem 1.25rem 1.56rem 1.25rem;
      text-align: center;

      .close--modal {
        position: absolute;
        top: 1.4rem;
        right: 1.4rem;
      }

      .modal-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1.25rem;

        .modal-title {
          margin-bottom: 0;
          text-transform: uppercase;
        }

        .modal-image-wrapper {
          width: 12rem;
          aspect-ratio: 2 / 3;
          overflow: hidden;
          display: flex;
          align-items: center;

          .modal-image {
            width: 100%;
            
            /deep/ img {
              width: 100%;
            }
          }
        }

        .modal-text {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          
          .modal-product-info {
            color: var(--zenith-grey);
            margin-bottom: 0;
            
            .modal-info-row {
              display: flex;
              align-items: center;
              justify-content: center;
              
              .modal-divider {
                width: 1px;
                height: 17px;
                background: var(--zenith-grey);
                margin-inline: 1rem;
              }
            }
          }
        }

        .button-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.625rem;
          width: fit-content;

          .button {
            width: 100%;
            text-transform: uppercase;
          }
        }
      }
    }
  }
}
</style>